'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { Button, CallToActionCard, Spinner } from '@benzinga/core-ui';
import { CallToAction } from '@benzinga/ui';
import { safeJsonFetch, SafeType } from '@benzinga/safe-await';
import { GeoManager } from '@benzinga/geo-manager';
import { States, getStateFromName } from '@benzinga/utils';
import { SessionContext } from '@benzinga/session-context';

export interface TransparentlyProps {
  params: Params;
  autoSearch: boolean | string;
  resultButtonText?: string;
  formButtonText?: string;
  geoType?: string;
  title: string;
  postTitle?: string;
}

interface Params {
  areaofstudy?: string;
  citizenship?: string;
  degreelevel?: string;
  dstf?: string;
  highestleveleducation?: string;
  hsgraduationyear?: string;
  pubcampaignid?: string;
  studylocation?: string;
  vertical?: string;
  zip_code?: string;
  state?: string;
}
interface Result {
  adCreativeId: number;
  position: number;
  title: string;
  paragraphDescription: string;
  logoUrl: string;
  displayUrl: string;
  clickUrl: string;
  publisherRevenue: number;
  brandName: string;
  advertiserId: number;
  bulletedDescription?: string[];
}

export const Transparently = ({
  autoSearch = false,
  formButtonText,
  geoType,
  params,
  postTitle,
  resultButtonText,
  title,
}: TransparentlyProps) => {
  const [zipCode, setZipCode] = React.useState<string>('');
  const [stateAbbreviation, setStateAbbreviation] = React.useState<string>();
  const [results, setResults] = React.useState<Result[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const formGeoType = geoType ? geoType : 'zipCode';

  const session = React.useContext(SessionContext);
  const geoManager = session.getManager(GeoManager);

  const handleFormSubmission = React.useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleFetchResults();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const handleFetchResults = React.useCallback(() => {
    setIsLoading(true);
    (async () => {
      let body = {} as Params;

      if (params) {
        body = params;
      }

      if (zipCode && formGeoType === 'zipCode') {
        body.zip_code = zipCode;
        body.state = stateAbbreviation;
      }

      if (stateAbbreviation && geoType === 'region') {
        body.state = stateAbbreviation;
      }

      const res: SafeType<{ result: Result[] }> = await safeJsonFetch('https://api.transparent.ly/search/blue/green', {
        body: JSON.stringify(body),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
      });

      if (res.ok) {
        setResults(res.ok.result as Result[]);
      }
      setIsLoading(false);
    })();
  }, [formGeoType, geoType, params, stateAbbreviation, zipCode]);

  const getGeoZip = React.useCallback(() => {
    (async () => {
      const _zipCode = await geoManager.getZipCode();
      const geoLocation = await geoManager.getGeoLocation();

      if (_zipCode.ok) {
        setZipCode(_zipCode.ok);
      }

      const state = getStateFromName(geoLocation?.ok?.region);

      if (state) {
        setStateAbbreviation(state?.abbreviation);
      }

      handleFetchResults();
    })();
  }, [geoManager, handleFetchResults]);

  React.useEffect(() => {
    // eslint-disable-next-line eqeqeq
    if (autoSearch == 'true' && !zipCode) {
      getGeoZip();
    }
  }, [autoSearch, getGeoZip, zipCode]);

  const handleChangeZipCode = React.useCallback((e: any) => {
    setZipCode(e.target.value);
  }, []);

  const handleChangeStateAbbreviation = React.useCallback((e: any) => {
    setStateAbbreviation(e.target.value);
  }, []);

  const getBulletedDescription = React.useCallback((result: Result) => {
    if (result.bulletedDescription) {
      let description = `<ul id="bulleted-description">`;
      result.bulletedDescription.forEach(desc => {
        description += `<li>${desc}</li>`;
      });
      description += '</ul>';
      return description;
    }
    return '';
  }, []);

  return (
    <Container className="transparently-container">
      <CallToActionCard className="transparently-card">
        <form onSubmit={handleFormSubmission}>
          <h5>{title}</h5>
          <div className="input-wrapper">
            {geoType === 'zipCode' && (
              <input
                className="zip-code border border-gray-300 px-2 focus:outline-none"
                name="zip_code"
                onChange={handleChangeZipCode}
                placeholder="Enter Zip Code"
                required={true}
                type="text"
                value={zipCode}
              />
            )}
            {geoType === 'region' && (
              <select className="state" name="state" onChange={handleChangeStateAbbreviation} value={stateAbbreviation}>
                <option>Select State</option>
                {States.map(state => {
                  return <option value={state.abbreviation}>{state.name}</option>;
                })}
              </select>
            )}
            <Button onClick={handleFetchResults} variant="primary">
              {formButtonText ?? 'GO'}
            </Button>
          </div>
        </form>
      </CallToActionCard>

      {isLoading && (
        <div className="p-8">
          <Spinner />
        </div>
      )}

      <div className="results-wrapper">
        {results.map((result, index) => (
          <CallToAction
            button_text={resultButtonText ?? 'Get Started'}
            description={`<p>${result.paragraphDescription ?? ''}</p>
            ${result.bulletedDescription ? getBulletedDescription(result) : ''}
            <span>${result.displayUrl || result.brandName}</span>`}
            descriptionLinesCount={result.bulletedDescription ? result.bulletedDescription.length + 2 : 5}
            image_url={result?.logoUrl}
            key={`call-to-action-item-${result.advertiserId}-${index}`}
            title={result.title}
            url={result.clickUrl}
            utm_action="Transparently"
            utm_category="Lead Click"
            utm_label={postTitle ?? 'Unknown'}
          />
        ))}
      </div>
    </Container>
  );
};

export const Container = styled.div`
  &.transparently-container {
    .transparently-card {
      margin-bottom: 1rem;
      .input-wrapper {
        height: 45px;
        width: 100%;
        display: flex;

        input {
          border-top-left-radius: 4px;
          border-bottom-left-radius: 4px;
          width: 100%;
        }

        input,
        button {
          height: 100%;
        }

        button {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
          border-left-width: 0;
          min-width: 64px;
        }
      }
    }
    #bulleted-description {
      margin-bottom: 14px;
    }
  }
`;

export default Transparently;
